import React, { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card'
import { useUnits } from '../../../contexts/UnitsContext'
import { useIsMobile } from '../../../hooks/useIsMobile'

const TOTAL_WEEKS = 53
const DAYS_PER_WEEK = 7

function toLocalDateString(date) {
  // YYYY-MM-DD in local timezone
  return date.toLocaleDateString('en-CA')
}

function buildGrid() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // End of grid: Saturday of the current week (so the current week's column is complete)
  const end = new Date(today)
  const dayOfWeek = end.getDay() // 0 = Sunday
  end.setDate(end.getDate() + (6 - dayOfWeek))

  // Start of grid: TOTAL_WEEKS-1 weeks before the end-of-week, on a Sunday
  const start = new Date(end)
  start.setDate(start.getDate() - (TOTAL_WEEKS * DAYS_PER_WEEK - 1))

  // Build a 2D array [week][day]
  const weeks = []
  const current = new Date(start)
  for (let w = 0; w < TOTAL_WEEKS; w++) {
    const week = []
    for (let d = 0; d < DAYS_PER_WEEK; d++) {
      week.push(new Date(current))
      current.setDate(current.getDate() + 1)
    }
    weeks.push(week)
  }

  return { weeks, today }
}

function computeMonthLabels(weeks) {
  // Show the abbreviated month name on the first column where a new month starts.
  // Only label the column if it contains the 1st-7th of that month.
  const labels = new Array(weeks.length).fill('')
  let lastMonth = -1
  for (let w = 0; w < weeks.length; w++) {
    const firstDay = weeks[w][0]
    const month = firstDay.getMonth()
    if (month !== lastMonth) {
      const containsFirstWeek = weeks[w].some((d) => d.getDate() <= 7 && d.getMonth() === month)
      if (containsFirstWeek) {
        labels[w] = firstDay.toLocaleDateString('en-US', { month: 'short' })
        lastMonth = month
      }
    }
  }
  return labels
}

const DAY_LABELS = ['', 'Mon', '', 'Wed', '', 'Fri', '']

export function HeatMapCalendar({ entries }) {
  const { t } = useTranslation()
  const { weightUnit, formatWeight } = useUnits()
  const isMobile = useIsMobile()
  const scrollRef = useRef(null)

  // On mobile, anchor scroll to today's column so the most recent
  // activity is visible without the user needing to swipe.
  useEffect(() => {
    if (!isMobile || !scrollRef.current) return
    const el = scrollRef.current
    el.scrollLeft = el.scrollWidth - el.clientWidth
  }, [isMobile])

  const { weeks, today, entriesByDate, totalLogged } = useMemo(() => {
    const { weeks, today } = buildGrid()
    const entriesByDate = new Map()
    for (const e of entries) {
      entriesByDate.set(e.date, e.weight)
    }

    const startDate = weeks[0][0]
    let totalLogged = 0
    for (const e of entries) {
      const d = new Date(e.date)
      if (d >= startDate && d <= today) totalLogged++
    }

    return { weeks, today, entriesByDate, totalLogged }
  }, [entries])

  if (!entries || entries.length === 0) return null

  const totalDays = TOTAL_WEEKS * DAYS_PER_WEEK
  const monthLabels = computeMonthLabels(weeks)

  // Flatten cells in column-major order so CSS grid with grid-auto-flow:column places them right
  const flatCells = []
  for (let w = 0; w < TOTAL_WEEKS; w++) {
    for (let d = 0; d < DAYS_PER_WEEK; d++) {
      flatCells.push({ date: weeks[w][d], weekIndex: w, dayIndex: d })
    }
  }

  // On mobile, force a readable cell size (14px) so dots are tappable;
  // overflow-x-auto on the outer container lets the user pan back through
  // history. On larger screens, the grid stays fluid as before.
  const gridTemplate = isMobile
    ? `repeat(${TOTAL_WEEKS}, 14px)`
    : `repeat(${TOTAL_WEEKS}, minmax(0, 1fr))`

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2 flex-wrap">
          <Activity className="w-4 h-4 text-app-primary-500" />
          {t('phoenixTracker.progress.activity')}
          <span className="text-sm font-normal text-muted-foreground">
            · {t('phoenixTracker.progress.daysLogged', { count: totalLogged, total: totalDays })}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div dir="ltr" className="w-full overflow-x-auto" ref={scrollRef}>
          {/* Two-column layout: day labels on left, content fills the rest */}
          <div className="flex gap-1.5 w-fit min-w-full">
            {/* Day-of-week labels column */}
            <div
              className="grid text-[10px] text-muted-foreground font-mono shrink-0"
              style={{
                gridTemplateRows: `auto repeat(${DAYS_PER_WEEK}, minmax(0, 1fr))`,
              }}
            >
              {/* Spacer to align with month labels row */}
              <div className="h-3.5" />
              {DAY_LABELS.map((label, i) => (
                <div key={i} className="flex items-center justify-end pe-1 leading-none">
                  {label}
                </div>
              ))}
            </div>

            {/* Right side: month labels + cell grid stacked */}
            <div className="flex-1 min-w-0">
              {/* Month labels row */}
              <div
                className="grid text-[10px] text-muted-foreground font-mono mb-1 h-3.5"
                style={{ gridTemplateColumns: gridTemplate }}
              >
                {monthLabels.map((label, w) => (
                  <div key={w} className="leading-none">
                    {label}
                  </div>
                ))}
              </div>

              {/* Cell grid: 53 columns × 7 rows, column-major flow */}
              <div
                className="grid gap-[3px]"
                style={{
                  gridTemplateColumns: gridTemplate,
                  gridTemplateRows: `repeat(${DAYS_PER_WEEK}, minmax(0, 1fr))`,
                  gridAutoFlow: 'column',
                }}
              >
                {flatCells.map(({ date }, i) => {
                  const dateStr = toLocalDateString(date)
                  const isFuture = date > today
                  const weight = entriesByDate.get(dateStr)
                  const hasEntry = weight != null

                  const dateLabel = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })
                  const tooltip = hasEntry
                    ? `${dateLabel} — ${formatWeight(weight)} ${weightUnit}`
                    : `${dateLabel} — ${t('phoenixTracker.progress.noEntry')}`

                  return (
                    <div
                      key={i}
                      title={isFuture ? '' : tooltip}
                      className={
                        isFuture
                          ? 'bg-transparent'
                          : hasEntry
                            ? 'bg-app-primary-500 hover:ring-2 hover:ring-app-primary-300 dark:hover:ring-app-primary-700 rounded-sm aspect-square'
                            : 'bg-gray-200 dark:bg-gray-800 hover:bg-gray-300 dark:hover:bg-gray-700 rounded-sm aspect-square'
                      }
                    />
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}
